import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Plus, X } from "lucide-react";
import {
  useCreatePatientConcerns,
  useUpdatePatientConcerns,
} from "@/hooks/useConsultation";
import { useToast } from "@/hooks/use-toast";
import type { PatientConcern } from "@/types/appointments";

interface AddPatientConcernDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appointmentId: string;
  patientConcerns?: PatientConcern[];
}

export const AddPatientConcernDialog = ({
  open,
  onOpenChange,
  appointmentId,
  patientConcerns = [],
}: AddPatientConcernDialogProps) => {
  const [currentConcern, setCurrentConcern] = useState("");
  const [allConcerns, setAllConcerns] = useState<string[]>([]);
  const { toast } = useToast();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const syncRef = useRef(false);

  const createMutation = useCreatePatientConcerns();
  const updateMutation = useUpdatePatientConcerns();

  const isLoading = createMutation.isPending || updateMutation.isPending;

  // Initialize concerns from existing patient concerns
  useEffect(() => {
    if (open) {
      // Prevent sync loops during active API additions
      if (!syncRef.current) {
        // Get all existing concerns from the current appointment
        const existingConcerns = patientConcerns
          .filter((c) => c.appointment === appointmentId && c.is_active)
          .flatMap((c) => c.concerns);

        // Remove duplicates
        const uniqueConcerns = Array.from(new Set(existingConcerns));
        setAllConcerns(uniqueConcerns);
        syncRef.current = true;
      }
    } else {
      // Reset tracker on close so it natively resyncs cleanly next open
      syncRef.current = false;
    }
  }, [open, appointmentId, patientConcerns]);

  // Only reset the input field when the dialog is initially opened
  useEffect(() => {
    if (open) {
      setCurrentConcern("");
    }
  }, [open]);

  const saveConcerns = async (concerns: string[]) => {
    try {
      const hasExistingConcerns =
        patientConcerns.length > 0 &&
        patientConcerns.some((c) => c.appointment === appointmentId);

      const isActive = concerns.length > 0;

      if (hasExistingConcerns) {
        await updateMutation.mutateAsync({
          appointmentId,
          data: {
            concerns,
            is_active: isActive,
          },
        });
      } else if (concerns.length > 0) {
        try {
          await createMutation.mutateAsync({
            appointmentId,
            data: {
              concerns,
              is_active: true,
            },
          });
        } catch (createError: any) {
          if (createError?.response?.status === 400) {
            await updateMutation.mutateAsync({
              appointmentId,
              data: {
                concerns,
                is_active: true,
              },
            });
          } else {
            throw createError;
          }
        }
      }
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "Failed to save patient concerns",
        variant: "destructive",
      });
    }
  };

  const handleAddConcern = async () => {
    const trimmed = currentConcern.trim();
    if (trimmed && !allConcerns.includes(trimmed)) {
      const updatedConcerns = [...allConcerns, trimmed];
      setAllConcerns(updatedConcerns);
      setCurrentConcern("");

      // Auto-snap textarea back to single line when cleared
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
      }

      await saveConcerns(updatedConcerns);
      toast({
        title: "Success",
        description: "Concern added successfully",
      });
    }
  };

  const handleRemoveConcern = async (index: number) => {
    const updatedConcerns = allConcerns.filter((_, i) => i !== index);
    setAllConcerns(updatedConcerns);
    await saveConcerns(updatedConcerns);
    toast({
      title: "Success",
      description: "Concern removed successfully",
    });
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCurrentConcern(e.target.value);
    const el = e.target;
    el.style.height = "auto";
    const maxHeight = 80;
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] h-[520px] flex flex-col p-0 gap-0 overflow-hidden border-border/60">
        <DialogHeader className="p-6 pb-0 shrink-0">
          <DialogTitle className="z-h2 text-foreground">
            Add Patient Concerns
          </DialogTitle>
        </DialogHeader>
        <Separator className="mt-3" />

        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {/* Already Added Section */}
          <div className="px-6 py-4 border-b border-border/60 shrink-0">
            <h3 className="z-label text-muted-foreground mb-3">
              Already Added
            </h3>
            <div className="rounded-xl border border-border/60 p-3 max-h-[200px] overflow-y-auto scrollbar-thin">
              {allConcerns.length > 0 ? (
                <div className="space-y-1.5">
                  {allConcerns.map((concern, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 px-3 py-2 bg-muted/50 rounded-lg animate-in fade-in zoom-in-95 duration-200"
                    >
                      <span className="z-body-md text-muted-foreground shrink-0">
                        {index + 1}.
                      </span>
                      <span className="flex-1 z-body text-foreground leading-snug break-words min-w-0">
                        {concern}
                      </span>
                      <button
                        onClick={() => handleRemoveConcern(index)}
                        className="shrink-0 p-1 rounded-md bg-background/80 text-muted-foreground hover:bg-destructive/20 hover:text-destructive transition-colors"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 z-body text-muted-foreground">
                  No patient concerns added yet
                </div>
              )}
            </div>
          </div>

          {/* Add New Concern Input */}
          <div className="px-6 py-4 bg-background">
            <div className="flex gap-2 items-center rounded-xl border border-border/60 bg-muted/30 p-2">
              <textarea
                ref={textareaRef}
                placeholder="Type a concern here..."
                value={currentConcern}
                onChange={handleTextareaChange}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleAddConcern();
                  }
                }}
                rows={1}
                className="flex-1 min-h-10 max-h-20 bg-transparent border-none resize-none px-3 py-2 z-body text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-0 scrollbar-thin"
              />
              <Button
                size="icon"
                onClick={handleAddConcern}
                disabled={
                  !currentConcern.trim() ||
                  allConcerns.includes(currentConcern.trim()) ||
                  isLoading
                }
                className="h-8 w-8 rounded-xl shrink-0"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
